import React, { useState, useRef, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import './ComplianceChat.css';

// Suggested queries for empty state
const SUGGESTED_QUERIES = [
  {
    category: 'Advertising',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
        <line x1="3" y1="9" x2="21" y2="9"/>
        <line x1="9" y1="21" x2="9" y2="9"/>
      </svg>
    ),
    queries: [
      'Can I use client testimonials on my website?',
      'Can I call myself a specialist in anxiety?'
    ]
  },
  {
    category: 'Code of Conduct',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    ),
    queries: [
      'What are my record-keeping obligations?',
      'Can I treat a friend as a client?'
    ]
  },
  {
    category: 'Mandatory Notifications',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
    queries: [
      'When do I need to report another practitioner?',
      'What counts as placing the public at substantial risk?'
    ]
  }
];

// Simple markdown to HTML converter
function parseMarkdown(text) {
  // Convert ### headings to bold
  text = text.replace(/^### (.+)$/gm, '<strong class="md-heading">$1</strong>');
  text = text.replace(/^## (.+)$/gm, '<strong class="md-heading">$1</strong>');

  // Convert **bold** to <strong>
  text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Convert numbered lists (1. 2. 3. etc)
  text = text.replace(/^(\d+)\. (.+)$/gm, '<span class="md-list-item"><span class="md-list-num">$1.</span> $2</span>');

  // Convert bullet points - to proper formatting
  text = text.replace(/^- (.+)$/gm, '<span class="md-bullet">• $1</span>');
  text = text.replace(/^• (.+)$/gm, '<span class="md-bullet">• $1</span>');

  // Convert paragraphs - double newlines become single break
  text = text.replace(/\n\n+/g, '<br/>');
  // Single newlines within content
  text = text.replace(/\n/g, ' ');

  return text;
}

// Strip feedback submission markers from display text
function extractFeedbackMarker(text) {
  const feedbackRegex = /\[\[submit-feedback:[^\]]+\]\]/g;
  const hasFeedback = feedbackRegex.test(text);
  const cleanText = text.replace(feedbackRegex, '').trim();
  return { cleanText, hasFeedback };
}

// Parse citations from response text
function parseCitations(text) {
  const segments = [];
  const citationRegex = /\[\[source:([^\|]+)\|([^\]]+)\]\]/g;
  let lastIndex = 0;
  let match;

  while ((match = citationRegex.exec(text)) !== null) {
    // Add text before citation
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: text.slice(lastIndex, match.index)
      });
    }

    // Add citation
    segments.push({
      type: 'citation',
      document: match[1].trim(),
      section: match[2].trim()
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.slice(lastIndex)
    });
  }

  return segments.length > 0 ? segments : [{ type: 'text', content: text }];
}

// Citation badge component
function CitationBadge({ document, section, onClick }) {
  return (
    <button className="citation-badge" onClick={onClick} title={`${document}: ${section}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
      </svg>
      {section}
    </button>
  );
}

// Message component
function ChatMessage({ message, onCitationClick }) {
  if (message.role === 'user') {
    return (
      <div className="chat-message user-message">
        <div className="message-content">
          {message.content}
        </div>
      </div>
    );
  }

  // Assistant message with citations (strip any leftover markers)
  const { cleanText } = extractFeedbackMarker(message.content);
  const segments = parseCitations(cleanText);

  return (
    <div className="chat-message assistant-message">
      <div className="message-avatar">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 16v-4"/>
          <path d="M12 8h.01"/>
        </svg>
      </div>
      <div className="message-content">
        {segments.map((segment, i) =>
          segment.type === 'citation' ? (
            <CitationBadge
              key={i}
              document={segment.document}
              section={segment.section}
              onClick={() => onCitationClick(segment)}
            />
          ) : (
            <span key={i} dangerouslySetInnerHTML={{ __html: parseMarkdown(segment.content) }} />
          )
        )}
        {message.feedbackSubmitted && (
          <span className="feedback-submitted-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Feedback submitted
          </span>
        )}
      </div>
    </div>
  );
}

// Streaming message component
function StreamingMessage({ content }) {
  const { cleanText } = extractFeedbackMarker(content);
  const segments = parseCitations(cleanText);

  return (
    <div className="chat-message assistant-message streaming">
      <div className="message-avatar">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 16v-4"/>
          <path d="M12 8h.01"/>
        </svg>
      </div>
      <div className="message-content">
        {segments.map((segment, i) =>
          segment.type === 'citation' ? (
            <CitationBadge
              key={i}
              document={segment.document}
              section={segment.section}
              onClick={() => {}}
            />
          ) : (
            <span key={i} dangerouslySetInnerHTML={{ __html: parseMarkdown(segment.content) }} />
          )
        )}
        <span className="cursor-blink">|</span>
      </div>
    </div>
  );
}

function ComplianceChat() {
  const {
    user,
    hasActiveSubscription,
    currentChatId,
    onSaveChat,
    onNewChat
  } = useOutletContext();

  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [chatId, setChatId] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch messages from Supabase when currentChatId changes
  useEffect(() => {
    if (!currentChatId) {
      setChatId(null);
      setMessages([]);
      return;
    }

    setChatId(currentChatId);
    setMessagesLoading(true);
    setError(null);

    fetch(`/api/chat-sessions?userId=${user.id}&sessionId=${currentChatId}`)
      .then(r => r.json())
      .then(data => {
        setMessages(data.messages || []);
      })
      .catch(err => {
        console.error('Failed to load messages:', err);
        setError('Failed to load conversation.');
      })
      .finally(() => setMessagesLoading(false));
  }, [currentChatId, user.id]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const message = inputValue.trim();
    if (!message || isLoading) return;

    // Check subscription
    if (!hasActiveSubscription) {
      setError('Compliance Chat is available for Basic and Pro subscribers. Upgrade to access this feature.');
      return;
    }

    setInputValue('');
    setError(null);
    setIsLoading(true);
    setStreamingContent('');

    // Add user message to list
    const userMessage = { role: 'user', content: message };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);

    try {
      const response = await fetch('/api/compliance-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          sessionId: chatId, // null for new conversations
          userId: user.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.requiresUpgrade) {
          setError('Compliance Chat requires a Basic subscription. Click here to upgrade.');
          setIsLoading(false);
          return;
        }
        throw new Error(errorData.error || 'Failed to send message');
      }

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let finalData = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.error) {
                throw new Error(data.error);
              }

              if (data.done) {
                finalData = data;
              } else if (data.content) {
                fullContent += data.content;
                setStreamingContent(fullContent);
              }
            } catch (parseError) {
              // Skip malformed JSON
            }
          }
        }
      }

      // Add completed assistant message
      if (fullContent) {
        const assistantMessage = {
          role: 'assistant',
          content: fullContent,
          sources: finalData?.sources || [],
          feedbackSubmitted: finalData?.feedbackSubmitted || false
        };

        const finalMessages = [...updatedMessages, assistantMessage];
        setMessages(finalMessages);

        // If this was a new session, update local state with the returned session ID
        if (finalData?.sessionId && !chatId) {
          setChatId(finalData.sessionId);
          onSaveChat({
            id: finalData.sessionId,
            title: finalData.title || message.substring(0, 50),
            timestamp: new Date().toISOString()
          });
        }
      }

    } catch (err) {
      console.error('Chat error:', err);
      setError(err.message || 'Failed to get response. Please try again.');
    } finally {
      setIsLoading(false);
      setStreamingContent('');
    }
  };

  const handleSuggestedQuery = (query) => {
    setInputValue(query);
    inputRef.current?.focus();
  };

  const handleCitationClick = (citation) => {
    // For now, just log it. Could open a modal with the full source text.
    console.log('Citation clicked:', citation);
  };

  // Check if user needs to upgrade
  const needsUpgrade = !hasActiveSubscription;

  return (
    <div className="compliance-chat">
      {/* Upgrade prompt for free users */}
      {needsUpgrade && (
        <div className="upgrade-prompt">
          <div className="upgrade-content">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="32" height="32">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
            <h3>Upgrade to Access Chat</h3>
            <p>Compliance Chat is available for Basic and Pro subscribers. Ask any compliance question and get instant, cited answers.</p>
            <button className="upgrade-btn" onClick={() => navigate('/pricing')}>
              View Plans
            </button>
          </div>
        </div>
      )}

      {/* Messages area */}
      <div className={`chat-messages ${needsUpgrade ? 'blurred' : ''}`}>
        {/* Loading state when switching conversations */}
        {messagesLoading && (
          <div className="chat-messages-loading">
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <p>Loading conversation...</p>
          </div>
        )}

        {!messagesLoading && messages.length === 0 && !streamingContent && !needsUpgrade && (
          <div className="chat-empty-state">
            <div className="empty-header">
              <h2>Ask me anything about compliance</h2>
              <p>I can help you understand AHPRA regulations, the Code of Conduct, and mandatory notification requirements.</p>
            </div>

            <div className="suggested-queries">
              {SUGGESTED_QUERIES.map((category, i) => (
                <div key={i} className="query-category">
                  <div className="category-header">
                    {category.icon}
                    <span>{category.category}</span>
                  </div>
                  <div className="category-queries">
                    {category.queries.map((query, j) => (
                      <button
                        key={j}
                        className="query-suggestion"
                        onClick={() => handleSuggestedQuery(query)}
                      >
                        {query}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!messagesLoading && messages.map((msg, i) => (
          <ChatMessage
            key={i}
            message={msg}
            onCitationClick={handleCitationClick}
          />
        ))}

        {streamingContent && (
          <StreamingMessage content={streamingContent} />
        )}

        {isLoading && !streamingContent && (
          <div className="chat-message assistant-message loading">
            <div className="message-avatar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4"/>
                <path d="M12 8h.01"/>
              </svg>
            </div>
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Error message */}
      {error && (
        <div className="chat-error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {error}
          {error.includes('Upgrade') && (
            <button className="error-upgrade-link" onClick={() => navigate('/pricing')}>
              Upgrade now
            </button>
          )}
        </div>
      )}

      {/* Input area */}
      <div className="input-separator" />
      <div className={`chat-input-container ${needsUpgrade ? 'disabled' : ''}`}>
        <div className="chat-input-content">
          <form onSubmit={handleSubmit} className="chat-form">
            <div className="chat-input-wrapper">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={needsUpgrade ? 'Upgrade to chat...' : 'Ask a compliance question...'}
                disabled={isLoading || needsUpgrade}
                className="chat-input"
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isLoading || needsUpgrade}
                className="chat-submit"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <line x1="22" y1="2" x2="11" y2="13"/>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
              </button>
            </div>
          </form>
          <p className="chat-disclaimer">
            Mindmark provides guidance only. Always verify with official AHPRA sources for critical decisions.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ComplianceChat;
